import { useEffect, useState } from "react";
import arrayUtilities from "../../../utilities/arrayUtilities";
import { Marker, useMap, useMapEvents } from "react-leaflet";
import { divIcon } from "leaflet";
import postal from "postal";
import ConfigurationDialog from "./_configurationDialog";
import ScaleSlider from "../../form/scaleSlider";
import MapPopup from "./_mapPopup";
import WorkflowSelect from "../../form/workflowSelect";
import geospatialUtilities from "../../../utilities/geospatialUtilities";
import stringUtilities from "../../../utilities/stringUtilities";
import cloneDeep from "lodash.clonedeep";
import LimitSelect from "../../form/limitSelect";
import sketchUtilities from "../../../utilities/sketchUtilities";

export default function StampDrawingTool(props) {

    const [isDrawing, setIsDrawing] = useState(props.isDrawing);
    const [isEditing, setIsEditing] = useState(props.isEditing);
    const [isVisible, setIsVisible] = useState(false);

    const [stamp, setStamp] = useState(null);

    const [printingSketchLimitIndex, setPrintingSketchLimitIndex] = useState(null);
    const [printingWorkflowId, setPrintingWorkflowId] = useState(null);
    const [shouldPrint, setShouldPrint] = useState(true);

    const [coords, setCoords] = useState(null);
    const [renderCoords, setRenderCoords] = useState(null);

    const [scale, setScale] = useState(null);
    const [workflowId, setWorkflowId] = useState(null);
    const [limitId, setLimitId] = useState(null);

    const map = useMap();

    const reset = function () {
        setCoords(props.sketchItem?.coords ?? []);
        setScale(props.sketchItem?.tool?.properties?.scale ?? 1);
        setWorkflowId(props.sketchItem?.tool?.properties?.workflowId ?? "");
        setLimitId(props.sketchItem?.tool?.properties?.limitId ?? props.activeLimit);
        
        let stampId = props.sketchItem?.tool?.properties?.stampId ?? props.stampId;
        setStamp(arrayUtilities.isNullOrEmpty(props.stamps) ? null : props.stamps.find(x => x.id === stampId));
    }

    const onDelete = function () {

        if (props.onDelete) {
            props.onDelete(props.index);
        }
    }

    const onCancel = function () {

        if (props.onCancel) {
            props.onCancel();
        }

        setIsDrawing(false);
        setIsEditing(false);
        reset();
    }

    const onSave = function () {

        if (props.onSave) {

            let sketchItem = {
                coords,
                tool: {
                    id: 24,
                    properties: {
                        scale,
                        workflowId,
                        limitId,
                        stampId : stamp?.id
                    }
                }
            }
            props.onSave(sketchItem, props.index);
        }

        setIsDrawing(props.isDrawing)
        setIsEditing(props.isEditing);
        reset();
    }

    const onMarkerDragEnd = function (e, index) {
        let copy = cloneDeep(coords);
        copy.splice(index, 1, e.target._latlng);
        setCoords(copy);
    }

    useMapEvents({
        click: (e) => {
            if (props.isDrawing) {
                setCoords(coords.concat([e.latlng]));
            }
        },
    })
    
    useEffect(() => {
        setRenderCoords(coords);
    }, [coords])

    useEffect(() => {

        if (isEditing) {
            postal.publish({
                channel: "map",
                topic: "isDrawingOrEditing",
                data: { value: true }
            })
        }

        map.closePopup();
    }, [isEditing])

    useEffect(() => {

        let pointsLinesIsDefault = arrayUtilities.isNullOrEmpty(props.workflowPoints) && arrayUtilities.isNullOrEmpty(props.workflowLines) && arrayUtilities.isNullOrEmpty(props.workflowPointsLines);
        let workflowIsVisible = stringUtilities.isNullOrEmpty(workflowId) || pointsLinesIsDefault || props.workflowPoints.includes(workflowId) || props.workflowLines.includes(workflowId) || props.workflowPointsLines.includes(workflowId);

        setIsVisible(isDrawing || isEditing || workflowIsVisible);
    }, [isDrawing, isEditing, workflowId, props.workflowLines, props.workflowPoints, props.workflowPointsLines])

    useEffect(() => {

        let shouldPrint = true;
        let renderCoords = coords;

        if (!arrayUtilities.isNullOrEmpty(coords) && ((printingSketchLimitIndex !== null) || !stringUtilities.isNullOrEmpty(printingWorkflowId))) {

            if (!sketchUtilities.isSketchObjectLimitCurrentlyPrinting(limitId, props.sketch?.limits, printingSketchLimitIndex)) {
                shouldPrint = false;
                renderCoords = [];
            }

            if (shouldPrint && !stringUtilities.isNullOrEmpty(workflowId)) {
                let workflow = arrayUtilities.isNullOrEmpty(props.workflows) ? null : props.workflows.find(x => x.id === workflowId);

                if (!stringUtilities.isNullOrEmpty(printingWorkflowId) && printingWorkflowId !== "ALL" && printingWorkflowId !== workflowId && (!workflow || workflow === null || !workflow.isComposite)) {
                    shouldPrint = false;
                    renderCoords = [];
                }
            }

            if (shouldPrint && printingSketchLimitIndex !== null && !isNaN(printingSketchLimitIndex) && props.sketch && props.sketch !== null && !arrayUtilities.isNullOrEmpty(props.sketch.limits) && props.sketch.limits.length > printingSketchLimitIndex) {
                let limit = props.sketch.limits[printingSketchLimitIndex];

                if (limit !== null && !arrayUtilities.isNullOrEmpty(limit.limitOfSketchCoords)) {
                    let losPolygon = geospatialUtilities.boundsToPolygonCoords(limit.limitOfSketchCoords);
                    renderCoords = coords.filter(x => geospatialUtilities.polygonContainsPoint(losPolygon, x));
                }
            }
        }

        setRenderCoords(renderCoords);

    }, [printingSketchLimitIndex, printingWorkflowId])

    useEffect(() => {
        reset();

        const subscriptions = [
            postal.subscribe({
                channel: "map",
                topic: "printing",
                callback: function (data) {
                    setPrintingSketchLimitIndex(data.sketchLimitIndex);
                    setPrintingWorkflowId(data.workflowId);
                }
            }),
        ]

        return () => {
            subscriptions.forEach(subscription => {
                subscription.unsubscribe();
            });
        }
    }, [])

    let icon = divIcon({
        html: `<img src="${stamp?.url}">`,
        className: "marker-icon-stamp",
        iconAnchor: [48 * (scale ?? props.symbolScale) * 0.5, 48 * (scale ?? props.symbolScale) * 0.5],
        iconSize: [48 * (scale ?? props.symbolScale), 48 * (scale ?? props.symbolScale)]
    });

    return <>

        {stamp && stamp !== null && !arrayUtilities.isNullOrEmpty(renderCoords) && isVisible &&  <>

            {renderCoords.map((coord, index) => <Marker
                index={index}
                key={`stamp-drawing-tool-point-${index}`}
                icon={icon}
                draggable={isDrawing || isEditing}
                position={[coord.lat, coord.lng]}
                bubblingMouseEvents={false}
                eventHandlers={{
                    dragend: (e) => onMarkerDragEnd(e, index)
                }}>
                {!isDrawing && !isEditing &&
                    <MapPopup title={`${stamp?.name} stamp`} onEdit={() => setIsEditing(true)} deleteTitle={`Delete ${stamp?.name} stamp`} deleteContent="Are you sure you want to permanently delete this stamp?" onDelete={onDelete} />
                }
            </Marker>)}
        </>}

        <ConfigurationDialog show={isDrawing || isEditing} title={`${isDrawing ? "Add" : "Edit"} ${stamp?.name} stamp`} onCancel={onCancel} onSave={onSave}>
            <ScaleSlider value={scale} onChange={setScale} max={10} />
            <WorkflowSelect value={workflowId} onChange={setWorkflowId} workflows={props.workflows} />
            <LimitSelect value={limitId} onChange={setLimitId} limits={props.sketch?.limits} />
        </ConfigurationDialog>
    </>
}